/**
 * The five-second checkpoint window, and the transitions that refuse to wait.
 *
 * Spec §4.5 rule 4 states the whole rule: *"Snapshot writes coalesce on a
 * 5-second window, but semantic transitions bypass it entirely."* and then
 * enumerates, per checkpoint shape, exactly which changes are semantic. Five
 * seconds is the **stated maximum loss window**, so the two halves of the rule
 * carry different risks and are wrong in different ways:
 *
 *  - Coalescing something semantic risks resuming into a state the user never
 *    left — a skipped combo that came back, a finished workout that is running
 *    again — which is worse than losing the write outright.
 *  - Writing everything immediately would put a full record encode plus a
 *    verified read-back on every 0.25 s display tick.
 *
 * So this file derives the urgency from *what changed*, not from how long it has
 * been. It is a pure function over two checkpoints, which is why it lives beside
 * the store rather than inside it: the engine holds the previous snapshot, and
 * the store has no business re-deriving the engine's own transitions.
 *
 * The trigger lists are exported because they are the contract. A field added to
 * a checkpoint is a decision about whether it may wait five seconds, and a list
 * that is only implicit in a chain of comparisons is a decision nobody makes.
 */

import type {
  EffortCheckpoint,
  Milliseconds,
  OpenEffortCheckpoint,
  SessionSegmentId,
  StructuredEffortCheckpoint,
} from "@ff/domain";
import type { CheckpointWriteUrgency } from "@ff/domain/ports";

import { canonicalJson } from "../kernel";

/**
 * The stated maximum loss window. Not a tuning knob: the user-facing promise is
 * that at most five seconds of a session can be lost to a crash, and changing
 * this number changes that promise.
 */
export const CHECKPOINT_COALESCING_WINDOW: Milliseconds = 5_000;

/**
 * The structured-checkpoint fields whose change is a semantic transition
 * (§4.5 rule 4). `remaining` is not here because only an *increase* in it is a
 * transition — that is the Back action — and a decrease is the clock running.
 */
export const STRUCTURED_IMMEDIATE_FIELDS = [
  "id",
  "schemaVersion",
  "startedAt",
  "plan",
  "segmentIndex",
  "isRunning",
  "isFinished",
  "skippedCombos",
  "combo",
  "activeDrill",
  "terminalOutcome",
  "executionLedger.completedSegmentIds",
  "executionLedger.skippedSegmentIds",
] as const;

/**
 * The open-effort equivalent. Neither duration is here because only a *decrease*
 * is a transition: time going forwards is the clock, time going backwards means
 * the effort was reconciled or reset and the older, larger value must not be the
 * one that survives a crash.
 */
export const OPEN_EFFORT_IMMEDIATE_FIELDS = [
  "id",
  "schemaVersion",
  "activity",
  "provenance",
  "startedAt",
  "isRunning",
  "isFinished",
  "terminalSession",
] as const;

/**
 * Whether `next` may wait for the window, given the snapshot it replaces.
 *
 * `previous === undefined` is always immediate: the first snapshot of an effort
 * establishes its id, and an effort whose very existence was still queued when
 * the tab died is an effort the user cannot resume.
 */
export function checkpointWriteUrgency(
  previous: EffortCheckpoint | undefined,
  next: EffortCheckpoint,
): CheckpointWriteUrgency {
  if (previous === undefined) return "immediate";
  // A shape change is a different store and a different effort. There is no
  // field-by-field comparison to make, and treating it as ordinary drift would
  // let a structured workout's first snapshot wait behind an open effort's.
  if (previous.shape !== next.shape) return "immediate";

  return next.shape === "structured"
    ? structuredUrgency(previous as StructuredEffortCheckpoint, next)
    : openEffortUrgency(previous as OpenEffortCheckpoint, next);
}

function structuredUrgency(
  previous: StructuredEffortCheckpoint,
  next: StructuredEffortCheckpoint,
): CheckpointWriteUrgency {
  if (previous.id !== next.id) return "immediate";
  if (previous.schemaVersion !== next.schemaVersion) return "immediate";
  if (previous.startedAt !== next.startedAt) return "immediate";
  if (!sameStructure(previous.plan, next.plan)) return "immediate";
  if (previous.segmentIndex !== next.segmentIndex) return "immediate";
  if (previous.isRunning !== next.isRunning) return "immediate";
  if (previous.isFinished !== next.isFinished) return "immediate";
  if (previous.skippedCombos !== next.skippedCombos) return "immediate";
  if (!sameStructure(previous.combo, next.combo)) return "immediate";
  if (previous.activeDrill !== next.activeDrill) return "immediate";
  if (previous.terminalOutcome !== next.terminalOutcome) return "immediate";

  if (
    !sameIdSet(
      previous.executionLedger.completedSegmentIds,
      next.executionLedger.completedSegmentIds,
    )
  ) {
    return "immediate";
  }
  if (
    !sameIdSet(
      previous.executionLedger.skippedSegmentIds,
      next.executionLedger.skippedSegmentIds,
    )
  ) {
    return "immediate";
  }

  // The Back action. Remaining time only ever falls while the clock runs, so a
  // rise means the user moved the cursor and the screen they would resume onto
  // is no longer the screen this checkpoint describes.
  if (next.remaining > previous.remaining) return "immediate";

  return "coalesced";
}

function openEffortUrgency(
  previous: OpenEffortCheckpoint,
  next: OpenEffortCheckpoint,
): CheckpointWriteUrgency {
  if (previous.id !== next.id) return "immediate";
  if (previous.schemaVersion !== next.schemaVersion) return "immediate";
  if (!sameStructure(previous.activity, next.activity)) return "immediate";
  if (!sameStructure(previous.provenance, next.provenance)) return "immediate";
  if (previous.startedAt !== next.startedAt) return "immediate";
  if (previous.isRunning !== next.isRunning) return "immediate";
  if (previous.isFinished !== next.isFinished) return "immediate";
  if (!sameStructure(previous.terminalSession, next.terminalSession)) return "immediate";

  if (next.wallDurationMilliseconds < previous.wallDurationMilliseconds) return "immediate";
  if (next.activeDurationMilliseconds < previous.activeDurationMilliseconds) return "immediate";

  return "coalesced";
}

/**
 * Set equality, not sequence equality, because §4.5 rule 4 says *"the ledger's
 * completed or skipped id **sets**"*. The ledger is cursor-independent — a user
 * stepping back and forward can reorder how ids were appended without changing
 * which segments were performed — and re-ordering alone is not a fact worth
 * losing a coalesced write over.
 */
function sameIdSet(
  previous: readonly SessionSegmentId[],
  next: readonly SessionSegmentId[],
): boolean {
  if (previous.length !== next.length) return false;
  const seen = new Set<SessionSegmentId>(previous);
  for (const id of next) {
    if (!seen.has(id)) return false;
  }
  return true;
}

/**
 * Deep equality by the same canonical encoding a write is verified against, so
 * "changed" here means exactly what "changed" means to storage.
 *
 * A value with no canonical form counts as *changed*. That is the safe
 * direction: the write will be refused either way, and refusing five seconds
 * from now instead of immediately would hide the fault behind a timer.
 */
function sameStructure(previous: unknown, next: unknown): boolean {
  if (previous === next) return true;
  if (previous === undefined || next === undefined) return false;
  try {
    return canonicalJson(previous) === canonicalJson(next);
  } catch {
    return false;
  }
}
